import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { fetchReactions } from "../../lib/api";

/**
 * Live emoji + vote reactions floating in the bottom-right corner of the 2nd screen.
 * Polls /api/reactions every 1.2s with since=<lastSeenAt> and animates each new
 * reaction with a fly-up motion.
 */
const ReactionsOverlay = () => {
  const [items, setItems] = useState([]);
  const sinceRef = useRef(new Date().toISOString());
  const seenIds = useRef(new Set());

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const data = await fetchReactions(sinceRef.current);
        if (stopped || !data?.length) return;
        const fresh = data.filter((r) => !seenIds.current.has(r.id));
        if (fresh.length) {
          fresh.forEach((r) => seenIds.current.add(r.id));
          sinceRef.current = fresh[fresh.length - 1].createdAt;
          setItems((prev) => [...prev, ...fresh]);
          // Drop each reaction after its animation finishes (4.5s)
          fresh.forEach((r) => {
            setTimeout(() => {
              setItems((p) => p.filter((x) => x.id !== r.id));
              seenIds.current.delete(r.id);
            }, 4800);
          });
        }
      } catch (e) {
        console.error("Failed to poll reactions", e);
      }
    };
    poll();
    const id = setInterval(poll, 1200);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div
      className="fixed bottom-6 right-6 z-30 pointer-events-none flex flex-col-reverse items-end gap-1 max-h-[60vh] overflow-hidden"
      data-testid="reactions-overlay"
    >
      {items.map((r) => (
        <ReactionBubble key={r.id} reaction={r} />
      ))}
    </div>
  );
};

const ReactionBubble = ({ reaction }) => {
  const isVote = reaction.kind === "vote";
  if (isVote) {
    // Internal scale is 1-10; show as 1-5 golden stars in a compact dark bubble.
    // No numbers, no fractions, no voter name. 4 & 5 stars use two centered lines.
    const stars = Math.max(1, Math.min(5, Math.round(Number(reaction.value) / 2)));
    const rows = stars <= 3 ? [stars] : stars === 4 ? [2, 2] : [2, 3];
    return (
      <div className="reaction-bubble flex items-center" data-testid={`reaction-${reaction.id}`}>
        <div className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-2xl bg-black/55 border border-white/15 backdrop-blur-md">
          {rows.map((count, ri) => (
            <div key={ri} className="flex items-center justify-center gap-0.5">
              {Array.from({ length: count }).map((_, i) => (
                <Star key={i} className="w-6 h-6 text-amber-400 drop-shadow" fill="currentColor" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="reaction-bubble flex items-center gap-2 max-w-[280px]" data-testid={`reaction-${reaction.id}`}>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md">
        <span className="text-3xl leading-none">{reaction.value}</span>
        {reaction.fromName && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/80 max-w-[140px] truncate">
            {reaction.fromName}
          </span>
        )}
      </div>
    </div>
  );
};

export default ReactionsOverlay;
